import { emptySpaceTime, matchSpaceTime, parseSpaceTime } from './spacetime.js';
import { emptySiteData } from '../sitedata/site.js';
import { slotToDateTime, toSlotKeyDecoded } from '../slotKey.js';
// Lazy cache for parsed defaultSpaceTime, keyed by serialized string.
// Since the serialized string is the authoritative source and parsing
// is deterministic, we can safely cache the result.
let _cachedSerialized = null;
let _cachedSpaceTime = null;
const getDefaultSpaceTime = (config) => {
    if (config.defaultSpaceTimeSerialized === _cachedSerialized && _cachedSerialized !== null) {
        return _cachedSpaceTime;
    }
    // Set cache to empty spacetime BEFORE parsing to break recursion.
    // parseSpaceTime → parseRefinedSpaceTime → applyDefaults calls
    // getDefaultSpaceTime again. During that re-entrant call, we return
    // the empty spacetime (matching the original behavior where
    // stConfig.defaultSpaceTime started as emptySpaceTime()).
    const sentinel = emptySpaceTime();
    _cachedSerialized = config.defaultSpaceTimeSerialized;
    _cachedSpaceTime = sentinel;
    const bootstrapSite = emptySiteData();
    bootstrapSite.spaceTimes = config;
    const parsed = parseSpaceTime({
        str: config.defaultSpaceTimeSerialized,
        site: bootstrapSite
    });
    _cachedSerialized = config.defaultSpaceTimeSerialized;
    _cachedSpaceTime = parsed;
    return parsed;
};
const emptySpaceTimeConfig = () => {
    return {
        defaultSpaceTimeSerialized: '',
        customSpaceTimes: {}
    };
};
const toSpaceTimeConfig = (raw) => {
    // Handle if null
    const rawish = raw ?? defaultSpaceTimeConfig();
    // If we don't have a defaultSpaceTimeSerialized, we need to set it
    if (!rawish.defaultSpaceTimeSerialized) {
        rawish.defaultSpaceTimeSerialized =
            defaultSpaceTimeConfig().defaultSpaceTimeSerialized;
    }
    const result = emptySpaceTimeConfig();
    result.defaultSpaceTimeSerialized = rawish.defaultSpaceTimeSerialized;
    if (rawish.customSpaceTimes) {
        result.customSpaceTimes = rawish.customSpaceTimes;
    }
    return result;
};
const defaultSpaceTimeConfig = () => {
    return {
        defaultSpaceTimeSerialized: 'man-søn@8-21',
        customSpaceTimes: {
            helg: 'fre@17-22 & lør & søn@08-16',
            hverdag: 'man-tor@8-22 & fre@8-16',
            kveld: 'man-søn@17-22'
        }
    };
};
// Spacetime defaults used when parsing access codes attached to a velpr
// access point. The code's spaceTime is just a *filter* on top of the AP
// (spaces are already constrained by the AP membership), so the natural
// default is "all days, all hours" — a fragment like "mon" should mean
// the whole Monday, not just the club's opening hours.
const velprAccessCodeSpaceTimeConfig = () => {
    return {
        defaultSpaceTimeSerialized: 'man-søn@0-23',
        customSpaceTimes: {}
    };
};
const emptyOpenCloseHourCache = () => ({
    opening: new Map(),
    closing: new Map()
});
const dateOrdinalKey = (dt) => dt.year * 1000 + dt.ordinal;
const defaultClosingHourAtDate = (dt, stConfig, cache) => {
    if (cache) {
        const key = dateOrdinalKey(dt);
        const cached = cache.closing.get(key);
        if (cached !== undefined)
            return cached;
        const result = defaultClosingHourAtDateUncached(dt, stConfig);
        cache.closing.set(key, result);
        return result;
    }
    return defaultClosingHourAtDateUncached(dt, stConfig);
};
const defaultClosingHourAtDateUncached = (dt, stConfig) => {
    const defaultSt = getDefaultSpaceTime(stConfig);
    for (let i = 23; i > 0; i--) {
        const isOpen = matchSpaceTime('', dt.set({ hour: i }), defaultSt);
        if (isOpen)
            return Math.min(23, i + 1);
    }
    // Else just return 23 as fallback
    return 23;
};
const defaultOpeningHourAtDate = (dt, stConfig, cache) => {
    if (cache) {
        const key = dateOrdinalKey(dt);
        const cached = cache.opening.get(key);
        if (cached !== undefined)
            return cached;
        const result = defaultOpeningHourAtDateUncached(dt, stConfig);
        cache.opening.set(key, result);
        return result;
    }
    return defaultOpeningHourAtDateUncached(dt, stConfig);
};
const defaultOpeningHourAtDateUncached = (dt, stConfig) => {
    const defaultSt = getDefaultSpaceTime(stConfig);
    for (let i = 0; i < 24; i++) {
        const isOpen = matchSpaceTime('', dt.set({ hour: i }), defaultSt);
        if (isOpen)
            return i;
    }
    // Else just return 8 as fallback
    return 8;
};
const defaultClosingHourAtItemKey = (slotKey, sst) => {
    const dt = slotToDateTime(toSlotKeyDecoded(slotKey));
    return defaultClosingHourAtDate(dt, sst);
};
const defaultOpeningHourAtItemKey = (slotKey, sst) => {
    const dt = slotToDateTime(toSlotKeyDecoded(slotKey));
    return defaultOpeningHourAtDate(dt, sst);
};
export { emptySpaceTimeConfig, getDefaultSpaceTime, toSpaceTimeConfig, defaultSpaceTimeConfig, velprAccessCodeSpaceTimeConfig, defaultOpeningHourAtDate, defaultOpeningHourAtItemKey, defaultClosingHourAtDate, defaultClosingHourAtItemKey, emptyOpenCloseHourCache };
//# sourceMappingURL=spacetimeConfig.js.map